// The end-of-volume marker, rendered in the page-turn footer's NEXT slot at the
// last unit of a bound volume (`shelf.ts`'s `volumeOfUnit`). Closing a volume is
// the one moment a reader knows they have finished one, so it is the one place
// the mark can be asked for without a settings screen — and where the next
// volume's page-turn sits directly beneath it.
//
// The mark is not merely a badge: a finished volume sinks to the back of its
// series' stack on the library shelf, so the copy says so. Read state lives in
// `read-marks.ts` (localStorage always, Convex too when signed in).

import { BookCheck, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useLogger } from "@/logging";
import { cn } from "@/lib/utils";
import type { CatalogBook } from "@bindery/shared/corpus/catalog";
import type { ReadingUnit } from "./reading-order";
import { useReadMarks } from "./read-marks";
import { lastChapterOf, volumeOfUnit } from "./shelf";

/**
 * The `PageTurnNav` slot for this unit: the mark control at a volume's last
 * unit, nothing anywhere else. Handed to the footer unconditionally, so the
 * two reading views don't each re-derive which unit ends a volume.
 */
export function VolumeEnd({
  book,
  unit,
}: {
  book: CatalogBook;
  unit: ReadingUnit;
}) {
  const volume = volumeOfUnit(book, unit);
  if (volume === null || !volume.atEnd) return null;
  // A volume of pure matter binds no chapter to key the mark on. Nothing on
  // the shelf can be in that state today (every edition holds chapters), and
  // if one ever were, offering an unstorable mark would be the wrong answer.
  const chapterNumber = lastChapterOf(volume.units);
  if (chapterNumber === null) return null;
  return (
    <VolumeEndMark
      bookSlug={book.slug}
      chapterNumber={chapterNumber}
      coverTitle={volume.edition.coverTitle}
      volumeLabel={volume.edition.volumeLabel}
    />
  );
}

function VolumeEndMark({
  bookSlug,
  /** Last chapter the volume binds — the mark's key (`convex/model/readMark.ts`). */
  chapterNumber,
  /** The title printed on this volume's cover. */
  coverTitle,
  /** "Volume Two", absent for a single-volume book. */
  volumeLabel,
}: {
  bookSlug: string;
  chapterNumber: number;
  coverTitle: string;
  volumeLabel?: string;
}) {
  const { isRead, setRead } = useReadMarks();
  const log = useLogger();
  const read = isRead(bookSlug, chapterNumber);

  const toggle = () => {
    const next = !read;
    setRead(bookSlug, chapterNumber, next);
    log.event("reading.volume_marked", {
      book: bookSlug,
      chapter: chapterNumber,
      read: next,
    });
    toast.success(next ? "Marked as read" : "Marked as unread", {
      description: next
        ? `${coverTitle} moves to the back of the shelf.`
        : `${coverTitle} returns to its place on the shelf.`,
    });
  };

  return (
    <div className="flex max-w-[16em] flex-col items-end gap-[0.6em] text-right">
      <span className="text-muted-foreground text-[0.65em] tracking-[0.25em] uppercase">
        {volumeLabel ? `End of ${volumeLabel.toLowerCase()}` : "The end"}
      </span>
      {/*
        Not the shadcn `Button`: everything in this footer is em-relative so it
        scales with the reader's text size (see `PageTurnNav`), and `Button`'s
        rem-based padding and text size would step out of that as soon as the
        reader moved the slider. Same affordances, expressed in ems — including
        the 44px-equivalent touch target `pointer-coarse` asks for, which at the
        smallest reading size still clears it.
      */}
      <button
        type="button"
        onClick={toggle}
        aria-pressed={read}
        className={cn(
          "group inline-flex items-center gap-[0.5em] rounded-[0.4em] border px-[0.9em] py-[0.5em]",
          "font-serif text-[0.8em] tracking-[0.12em] uppercase",
          "transition-colors motion-reduce:transition-none",
          "focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:outline-none",
          read
            ? "border-foreground bg-foreground text-background"
            : "border-border text-muted-foreground hover:border-foreground/50 hover:text-foreground",
        )}
      >
        {read ? (
          <BookCheck aria-hidden="true" className="size-[1.15em]" />
        ) : (
          <BookOpen aria-hidden="true" className="size-[1.15em]" />
        )}
        {read ? "Read" : "Mark as read"}
      </button>
      <span className="text-muted-foreground text-[0.7em] leading-snug text-pretty">
        {read
          ? "Shelved at the back of its set."
          : "Shelves it at the back of its set."}
      </span>
    </div>
  );
}
